import type { ReactNode } from 'react';
import Card from './Card';

/**
 * Titled section on a detail / dashboard page. Consolidates the ad-hoc
 * "heading + bg-white card" pattern that had fragmented across User Detail,
 * Match Detail, and Questions Edit.
 *
 * Use `inset={false}` when the body content is itself a card (e.g. a
 * `<DataTable>` which already has its own surface) — the title sits loose
 * above the body so we don't stack two borders.
 */

export interface PageSectionProps {
  title: ReactNode;
  /** Right-aligned controls on the title row (button, stats, timestamps). */
  actions?: ReactNode;
  /** Wrap body in a Card by default (set false when body already has one). */
  inset?: boolean;
  /** Padding for the inset Card. Ignored when `inset=false`. */
  padding?: 'sm' | 'md' | 'lg';
  /** Vertical spacing between immediate children of the body. */
  bodySpacing?: 'tight' | 'normal' | 'loose';
  testId?: string;
  children: ReactNode;
}

const BODY_SPACING: Record<'tight' | 'normal' | 'loose', string> = {
  tight: 'space-y-2',
  normal: 'space-y-3',
  loose: 'space-y-4',
};

export default function PageSection({
  title,
  actions,
  inset = true,
  padding = 'md',
  bodySpacing = 'normal',
  testId,
  children,
}: PageSectionProps) {
  const body = (
    <div className={BODY_SPACING[bodySpacing]}>{children}</div>
  );
  return (
    <section className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {inset ? <Card padding={padding}>{body}</Card> : body}
    </section>
  );
}
